<template>
  <div
    data-component="base-search-input"
    :class="states"
    @click.stop="onFocus"
  >
    <form
      data-form="search"
      @submit.prevent="onSearchSubmit"
      @click.prevent
    >
      <base-search-query
        :query="query"
        :schema="schema"
        @delete-param="onDeleteParam"
      >
        <template #input>
          <base-search-input-wrapper
            :query="query"
            :schema="schema"
            :search="search"
            :is-in-focus="isInFocus"
            :is-options-popup-open="isOptionsPopupOpen"
            @search-change="search = $event"
            @query-change="$emit('query-change', $event)"
          />
        </template>
      </base-search-query>
    </form>

    <div
      data-button="clear-query"
      :class="{ 'is-clickable': icon === 'clear' }"
      @click.stop="onClearQuery"
    >
      <or-icon :icon="icon" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';

import BaseSearchQuery from './BaseSearchQuery.vue';
import BaseSearchInputWrapper from './BaseSearchInputWrapper.vue';
import { QueryParam } from '../../types';
import { useSearch } from '../../composables';
import { getDefaultSubmitKey } from '../../common';
import { QUERY_PROP, SCHEMA_PROP } from '../../constants';

export default defineComponent({
  name: 'BaseSearchInput',
  components: {
    OrIcon,
    BaseSearchQuery,
    BaseSearchInputWrapper,
  },
  props: {
    defaultSubmitKey: {
      type: String,
      default: '',
    },
    query: QUERY_PROP,
    schema: SCHEMA_PROP,
    isDisabled: Boolean,
  },
  emits: ['query-change'],
  setup(props) {
    const { activeQueryParam, isActiveQueryParamComplete } = useSearch(props);
    return { activeQueryParam, isActiveQueryParamComplete };
  },
  data() {
    return {
      search: '',
      isInFocus: false,
      isOptionsPopupOpen: false,
    };
  },
  computed: {
    states() {
      return {
        'is-in-focus': this.isInFocus,
        'is-disabled': this.isDisabled,
      };
    },
    icon() {
      return this.query.length || this.search.length ? 'clear' : 'search';
    },
  },
  watch: {
    /** Clear focus when user enters or selects a full query param */
    isActiveQueryParamComplete(isComplete, wasComplete) {
      if (!wasComplete && isComplete) {
        this.onBlur();
      }
    },
  },
  created() {
    document.addEventListener('click', this.onBlur);
  },
  beforeDestroy() {
    document.addEventListener('click', this.onBlur);
  },
  methods: {
    onFocus() {
      this.isInFocus = true;
      this.isOptionsPopupOpen = !this.isOptionsPopupOpen;
    },

    onBlur() {
      this.isInFocus = false;
      this.isOptionsPopupOpen = false;
    },

    onDeleteParam(param: QueryParam) {
      const query = this.query.filter(p => p.key !== param.key);
      this.$emit('query-change', query);
    },

    onSearchSubmit() {
      const search = this.search.trim();
      if (!search) {
        return this.search = '';
      }

      let query = [];

      if (!this.activeQueryParam) {
        let defaultSubmitKey = this.defaultSubmitKey || getDefaultSubmitKey(this.schema);
        if (!defaultSubmitKey) {
          return;
        }

        query = [...this.query];
        query.push({ key: defaultSubmitKey, value: search });
      } else {
        const param = { ...this.activeQueryParam, value: search };
        query = [...this.query.slice(0, -1), param];
      }

      this.search = '';
      this.isInFocus = false;
      this.isOptionsPopupOpen = false;
      this.$emit('query-change', query);
    },

    onClearQuery() {
      this.search = '';
      this.isInFocus = false;
      this.isOptionsPopupOpen = false;
      this.$emit('query-change', []);
    },
  },
});
</script>

<style lang="scss" scoped>
@use '../../styles/' as *;

[data-component='base-search-input'] {
  --base-search-input-height: 2.25rem;

  display: flex;
  gap: var(--s-3);
  min-height: var(--base-search-input-height);
  padding: var(--s-2) 0.75rem;
  border: 1px solid var(--or-c-outline);
  border-radius: var(--s-2);

  &.is-in-focus:not(.is-disabled) {
    border-color: var(--or-c-primary);
  }

  .is-disabled {
    border-color: var(--or-c-disabled);
  }

  [data-form='search'] {
    display: flex;
    flex: 1 1 100%;
    overflow: hidden;
  }

  /* Clear button appears centered when query is limited to a single line */
  [data-button='clear-query'] {
    @include base-search-input-icon-button($size: 1.25rem);

    &:not(.is-clickable) {
      color: var(--or-c-outline) !important;
      cursor: default;
    }

    align-items: center;
    height: 1.625rem;
    margin-left: auto;
  }
}
</style>
